/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {BehaviorSubject, Observable} from 'rxjs';

/** A single step of a guided tour. */
export interface GuidedTourStep {
  /**
   * CSS selector of the element to highlight. When omitted (or the element is not found at
   * runtime), the step is shown centered without a spotlight (useful for a welcome step).
   * When several elements match, the first *visible* one is used.
   */
  selector?: string;
  /**
   * Optional function returning the viewport-relative rectangle to spotlight, for targets that are
   * not DOM elements (e.g. a node drawn on a canvas). Takes precedence over `selector`.
   */
  getRect?: () => { top: number; left: number; width: number; height: number } | null;
  /** Translation key for the step title. */
  titleKey: string;
  /** Translation key for the step body text. */
  textKey: string;
  /**
   * Optional hook run when the step becomes active, before it is positioned (e.g. to open a
   * dropdown that the step then highlights).
   */
  beforeShow?: () => void;
  /** Optional hook run when leaving the step or ending the tour (e.g. to close that dropdown). */
  onLeave?: () => void;
}

/** Current state of the active guided tour, or null when no tour is running. */
export interface GuidedTourState {
  tourId: string;
  step: GuidedTourStep;
  index: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
}

/**
 * Drives lightweight, in-app guided tours (a.k.a. coach marks). Tours are shown only once per
 * user/browser thanks to a localStorage flag, so first-time users are introduced to new UI
 * (e.g. the employee/organization sidebar) without bothering returning users.
 */
@Injectable({providedIn: 'root'})
export class GuidedTourService {

  private static readonly STORAGE_PREFIX = 'gp_tour_seen_';

  private tourId: string = null;
  private steps: GuidedTourStep[] = [];
  private currentIndex = -1;

  private readonly stateSubject = new BehaviorSubject<GuidedTourState>(null);

  /** Emits the current tour state, or null when no tour is active. */
  public get state$(): Observable<GuidedTourState> {
    return this.stateSubject.asObservable();
  }

  /** Whether the given tour has already been completed/skipped on this browser. */
  public hasSeen(tourId: string): boolean {
    try {
      return localStorage.getItem(GuidedTourService.STORAGE_PREFIX + tourId) === 'true';
    } catch {
      return false;
    }
  }

  /** Whether a tour is currently running. */
  public get isActive(): boolean {
    return this.currentIndex >= 0;
  }

  /** Starts a tour only if it has not been seen yet and no tour is currently running. */
  public startOnce(tourId: string, steps: GuidedTourStep[]): boolean {
    if (this.hasSeen(tourId) || this.isActive || !steps || !steps.length) {
      return false;
    }
    this.start(tourId, steps);
    return true;
  }

  /** Starts (or restarts) a tour, regardless of whether it was already seen. */
  public start(tourId: string, steps: GuidedTourStep[]): void {
    if (!steps || !steps.length) {
      return;
    }
    this.tourId = tourId;
    this.steps = steps;
    this.currentIndex = 0;
    this.emit();
  }

  /** Advances to the next step, or completes the tour when on the last step. */
  public next(): void {
    if (this.currentIndex < 0) {
      return;
    }
    if (this.currentIndex >= this.steps.length - 1) {
      this.complete();
      return;
    }
    this.currentIndex++;
    this.emit();
  }

  /** Goes back to the previous step (no-op on the first step). */
  public previous(): void {
    if (this.currentIndex <= 0) {
      return;
    }
    this.currentIndex--;
    this.emit();
  }

  /** Skips to the requested step index (used to auto-skip steps whose target is missing). */
  public goTo(index: number): void {
    if (index < 0 || index > this.steps.length - 1) {
      this.complete();
      return;
    }
    this.currentIndex = index;
    this.emit();
  }

  /** Ends the tour and marks it as seen so it won't show again. */
  public complete(): void {
    if (this.tourId) {
      try {
        localStorage.setItem(GuidedTourService.STORAGE_PREFIX + this.tourId, 'true');
      } catch {
        // Ignore storage errors (e.g. private mode); the tour simply may show again.
      }
    }
    this.reset();
  }

  private reset(): void {
    this.tourId = null;
    this.steps = [];
    this.currentIndex = -1;
    this.stateSubject.next(null);
  }

  private emit(): void {
    const step = this.steps[this.currentIndex];
    this.stateSubject.next({
      tourId: this.tourId,
      step,
      index: this.currentIndex,
      total: this.steps.length,
      isFirst: this.currentIndex === 0,
      isLast: this.currentIndex === this.steps.length - 1
    });
  }
}




